<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { ExternalLink, GitBranch, Folder as FolderIcon, Pencil, Clock, LayoutGrid, MessageSquare, AlertTriangle, FileText, ShieldAlert } from 'lucide-svelte';
  import { projectsApi } from '$lib/api/projects';
  import { membersApi } from '$lib/api/members';
  import { adminUsersApi } from '$lib/api/admin';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { pretty, projKind } from '$lib/utils/format';
  import Chip from '$lib/components/ui/Chip.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import ProjectModal from '$lib/components/admin/ProjectModal.svelte';
  import KanbanBoard from './KanbanBoard.svelte';
  import ProjectTicketsTab from './ProjectTicketsTab.svelte';
  import ProjectDebtTab from './ProjectDebtTab.svelte';
  import ProjectDocsTab from './ProjectDocsTab.svelte';
  import ProjectActivityTab from './ProjectActivityTab.svelte';
  import type { AdminUser } from '$lib/types';

  const { projectId }: { projectId: number } = $props();
  const admin = $derived(session.isAdmin);

  const projectsQuery = createQuery(() => ({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list()
  }));
  const project = $derived(projectsQuery.data?.results.find((p) => p.id === projectId));
  const accessible = $derived(!!session.me?.projects.some((p) => p.id === projectId));

  const usersQuery = createQuery(() => ({
    queryKey: ['admin-users', { search: '' }],
    queryFn: () => adminUsersApi.list({ per_page: 100 }),
    enabled: admin
  }));
  const membersQuery = createQuery(() => ({
    queryKey: ['project-members', projectId],
    queryFn: () => membersApi.forProject(projectId)
  }));
  const members = $derived(membersQuery.data?.results ?? []);

  const TAB_DEFS = [
    { key: 'board', icon: LayoutGrid },
    { key: 'tickets', icon: MessageSquare },
    { key: 'debt', icon: AlertTriangle },
    { key: 'docs', icon: FileText },
    { key: 'activity', icon: Clock }
  ] as const;
  const STATIC_LABELS: Partial<Record<(typeof TAB_DEFS)[number]['key'], string>> = {
    board: 'Board',
    debt: 'Tech debt',
    docs: 'Docs',
    activity: 'Activity'
  };
  function tabLabel(key: (typeof TAB_DEFS)[number]['key']): string {
    if (key === 'tickets') return admin ? 'Tickets' : 'My tickets';
    return STATIC_LABELS[key] ?? key;
  }
  let tab = $state<(typeof TAB_DEFS)[number]['key']>('board');

  let showEditModal = $state(false);
  let editMembers = $state<AdminUser[]>([]);
  function openEdit() {
    editMembers = (usersQuery.data?.results ?? []).filter((u) => u.role === 'member');
    showEditModal = true;
  }
</script>

{#if !accessible}
  <EmptyState icon={ShieldAlert} heading="No access" sub="This account hasn’t been granted access to this project.">
    {#snippet action()}
      <button
        type="button"
        class="btn btn-secondary"
        onclick={() => route.push(session.me?.projects[0] ? { tab: 'project', projectId: session.me.projects[0].id } : { tab: 'overview' })}
      >
        Go to my workspace
      </button>
    {/snippet}
  </EmptyState>
{:else if !project}
  <p style="color:var(--t3);font-size:13.5px">Loading project…</p>
{:else}
  <div class="pw-head">
    <div class="pw-top">
      <div style="flex:1;min-width:0">
        <div class="pw-name">{project.name}</div>
        <div class="pw-links">
          <Chip label={pretty(project.status)} kind={projKind(project.status)} dot />
          {#if project.prod_url}
            <a class="linkchip" href={project.prod_url} target="_blank" rel="noreferrer">
              <ExternalLink size={15} />{project.prod_url.replace(/^https?:\/\//, '')}
            </a>
          {/if}
          {#if project.repo}
            <a class="linkchip mono" href="https://github.com/{project.repo}" target="_blank" rel="noreferrer">
              <GitBranch size={15} />{project.repo}
            </a>
          {/if}
          {#if project.ref_erp}<Chip label={`ERP ${project.ref_erp}`} kind="gray" />{/if}
          {#if project.ref_hw_rev}<Chip label={project.ref_hw_rev} kind="gray" />{/if}
          {#if project.ref_artifact}
            <a class="linkchip" href="https://{project.ref_artifact}" target="_blank" rel="noreferrer">
              <FolderIcon size={15} />Artifacts
            </a>
          {/if}
        </div>
      </div>
      {#if admin}
        <button type="button" class="btn btn-secondary" style="flex-shrink:0" onclick={openEdit}>
          <Pencil size={15} />Edit project
        </button>
      {/if}
    </div>
    <div class="tabs">
      {#each TAB_DEFS as t (t.key)}
        <button type="button" class="tab {tab === t.key ? 'active' : ''}" onclick={() => (tab = t.key)}>
          <t.icon size={16} />{tabLabel(t.key)}
        </button>
      {/each}
    </div>
  </div>

  {#if tab === 'board'}
    <KanbanBoard {project} />
  {:else if tab === 'tickets'}
    <ProjectTicketsTab {projectId} {members} />
  {:else if tab === 'debt'}
    <ProjectDebtTab {projectId} />
  {:else if tab === 'docs'}
    <ProjectDocsTab {projectId} />
  {:else if tab === 'activity'}
    <ProjectActivityTab {projectId} />
  {/if}
{/if}

{#if showEditModal && project}
  <ProjectModal {project} members={editMembers} onClose={() => (showEditModal = false)} />
{/if}
